"use client";

/**
 * P2-06 · The `<ul>` of cards a product block renders, and the block's single
 * client boundary.
 *
 * P17A-08 put the boundary here rather than around the whole frame or around
 * each card: one `products` array pays the React-element framing once, where a
 * per-card crossing pays it per card.
 *
 * P18-19 · `<ProductCard>` no longer *needs* a boundary — it is a hook-free
 * shell now — so this directive is a deliberate byte choice, not a requirement.
 * A server component's markup is serialised into the inlined RSC payload as
 * well as the HTML, and a card is ~5.1 KB of markup built from ~1.1 KB of data;
 * measured on the demo homepage, letting the shells render on the server cost
 * **+50 KB** on a 271 KB document. So the cards cross once per block and their
 * markup is written once. See `docs/storefront-performance.md`
 * § "Where a product card's bytes go" for the decomposition.
 */
import { ProductCard } from "~/components/storefront/product-card";
import type { ProductCardView } from "~/components/storefront/product-card";
import { Badge } from "~/components/ui/badge";
import type { ProductsBlockLayoutKind } from "./products-block-view";

const GRID = "grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-4";
// Row: a horizontal snap-scroller; cards are fixed-width and swipe on touch.
// The cards are links, so keyboard focus moves through them and scrolls the
// row into view — scrollable without trapping focus.
const ROW =
  "-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0 " +
  // scroll-px-4: with snap-mandatory + snap-start, the first card snapped to the
  // scrollport edge and ate the row's own 16 px padding on phones (heading at
  // 16 px, card at 0). Scroll padding makes the snap positions honour it.
  "scroll-px-4 sm:scroll-px-0 " +
  "[scrollbar-width:thin] [&>li]:w-44 [&>li]:shrink-0 [&>li]:snap-start sm:[&>li]:w-56";

export function ProductsGrid({
  title,
  layout,
  products,
  rankBadges = false,
}: {
  /** `aria-label` for the list — the block's own title. */
  title: string;
  layout: ProductsBlockLayoutKind;
  products: ProductCardView[];
  /** Overlay a "#N" rank badge on each card (best-sellers, P2-06d). */
  rankBadges?: boolean;
}) {
  return (
    <ul aria-label={title} className={layout === "grid" ? GRID : ROW}>
      {products.map((product, i) => (
        <li key={product.id} className={rankBadges ? "relative" : undefined}>
          {rankBadges && (
            <span className="absolute left-2 top-2 z-[2]">
              <Badge tone="primary">#{i + 1}</Badge>
            </span>
          )}
          <ProductCard product={product} variant="grid" />
        </li>
      ))}
    </ul>
  );
}
